import React, { Component } from "react";
import { Modal, Form, notification } from "antd";
import Axios from "axios";
import moment from "moment";
import DeliveryDetails from "./DeliveryDetails";
import {
  DELIVERY_OPTIONS,
  OUTCOME_OPTIONS,
  PLACE_OF_DELIVERY_OPTIONS,
  Save_Anc_Details,
  Get_Anc_Details,
  Get_Obs_Gyane_Data,
  OBJECTIVE_GETALL,
  LOADER,
} from "../../../utils/constant";
import { speechService, speechTextService, pregnantService } from "../../../utils/rxjs-sharing";
import LS_SERVICE from "../../../utils/localStorage";

/**
 * DeliveryDetailsModal
 *
 * Hosts DeliveryDetails with its own Form, state, handlers and Save button.
 *
 * - Year = current year (disabled); Gestational Age derived from LMP (disabled).
 * - Common delivery fields captured once; per-fetus birth details captured per fetus
 *   (driven by No. of Fetus from the obs-gyane data).
 * - Per-fetus birth details are ALWAYS saved to the multiple_pregnancies column
 *   (one row per fetus), so single- and multi-fetus deliveries are fetched the same way.
 */
class DeliveryDetailsModal extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
    this.state = {
      allowEdit: props.allowEdit !== undefined ? props.allowEdit : true,
      gender_options: [
        { val: "1", label: "Male" },
        { val: "2", label: "Female" },
        { val: "3", label: "Transgender" },
      ],
      delivery_options: DELIVERY_OPTIONS,
      outcome_options: OUTCOME_OPTIONS,
      place_options: PLACE_OF_DELIVERY_OPTIONS,
      speech: { identifier: null, state: false },
      spin_loading: false,
      no_of_fetus: 1,
      // common (whole-delivery) fields
      pregnancy_year: new Date().getFullYear(),
      delivery_gestational_age: null,
      delivery_mode: null,
      delivery_type: null,
      delivery_place: null,
      delivery_outcome: null,
      delivery_indication_for_c_section: null,
      caesarean_section_type: null,
      delivery_details: null,
      // per-fetus birth details
      fetuses: [this.blankFetus()],
    };
  }

  blankFetus = () => ({
    delivery_bith_date_time: null,
    delivery_gender: null,
    delivery_birth_weight: null,
    delivery_birth_height: null,
    delivery_birth_head_cc: null,
    delivery_congenital_abnormality: false,
    delivery_congenital_abnormality_remarks: null,
  });

  async componentDidMount() {
    this.subscription = speechTextService.status().subscribe((data) => {
      if (this.state.speech.state && this.state.speech.identifier) {
        this.formRef?.current?.setFieldsValue({ [this.state.speech.identifier]: data.text });
        this.setState({ [this.state.speech.identifier]: data.text });
        const el = document.querySelector("#" + this.state.speech.identifier);
        if (el) el.style.height = el.scrollHeight + "px";
      }
    });

    // Derive Gestational Age + No. of Fetus from obs-gyane, then load saved delivery details.
    await this.loadObsGyaneContext();
    await this.getPatientAncDetails();
    // Fetch the patient's current allergies & conditions from the shared source (same as the
    // ANC card page), so closing the pregnancy persists them onto the closed card even when
    // opened from the single screen (where they aren't already on the ANC card row).
    await this.loadAllergiesAndConditions();
  }

  // Loads patient-level allergies & conditions (OBJECTIVE_GETALL) - the same source the ANC
  // card page uses to build current_allergies / current_conditions for the save payload.
  loadAllergiesAndConditions = async () => {
    try {
      const patient_id = parseInt(LS_SERVICE.get("PATIENT_REG_DATA").registrationid);
      const res = await Axios.get(OBJECTIVE_GETALL({ patient_id }));
      if (res?.data?.status) {
        const details = res.data?.data?.details || {};
        const allergies = details?.allergies?.patientAllergies || [];
        const conditions = details?.conditions?.patientConditions || [];
        await this.setState({
          current_allergies_snapshot: (allergies && allergies.length > 0) ? JSON.stringify(allergies) : this.state.current_allergies_snapshot,
          current_conditions_snapshot: (conditions && conditions.length > 0) ? JSON.stringify(conditions) : this.state.current_conditions_snapshot,
        });
      }
    } catch (e) {
      // Non-blocking: fall back to whatever was captured from Get_Anc_Details.
    }
  };

  componentWillUnmount() {
    this?.subscription?.unsubscribe();
  }

  buildWhereCondition = () => ({
    patient_id: parseInt(LS_SERVICE.get("PATIENT_REG_DATA").registrationid),
    reference_id: parseInt(LS_SERVICE.get("call_patient_qms_token_id")),
    reference_type: 0,
    hospital_id: parseInt(LS_SERVICE.get("FACILITY_ID")),
    enterprise_id: parseInt(LS_SERVICE.get("ENTERPRISE_ID")),
    source: "OP",
  });

  loadObsGyaneContext = async () => {
    try {
      const res = await Axios.post(Get_Obs_Gyane_Data, this.buildWhereCondition());
      if (res?.data?.status === true) {
        const d = res.data?.data;
        const lmp = d?.menstural_data?.lmp || null;
        const edd = d?.corrected_edd || d?.edd_by_lmp || null;
        const ga = this.computeGA(lmp, edd);
        const noOfFetus = parseInt(d?.no_of_fetus) > 0 ? parseInt(d.no_of_fetus) : 1;
        await this.setState({
          delivery_gestational_age: ga,
          no_of_fetus: noOfFetus,
          // Snapshot of the current pregnancy (obs & gynae) data, persisted on the ANC card
          // when the pregnancy is closed so the closed card keeps the full history.
          obs_gyane_data_snapshot: d || null,
        });
        this.formRef?.current?.setFieldsValue({ delivery_gestational_age: ga });
      }
    } catch (e) {
      // Non-blocking.
    }
  };

  computeGA = (lmp, edd) => {
    let totalDays = null;
    // DATE-ONLY diff (normalise to YYYY-MM-DD) so a time/timezone component on the stored
    // LMP/EDD can't shift the calendar day and inflate GA by one; keeps this identical to
    // the ANC card's getGAToday.
    const today = moment().format("YYYY-MM-DD");
    if (lmp && moment(lmp).isValid()) {
      totalDays = moment(today).diff(moment(moment(lmp).format("YYYY-MM-DD")), "days");
    }
    if ((totalDays === null || isNaN(totalDays)) && edd && moment(edd).isValid()) {
      const daysToEdd = moment(moment(edd).format("YYYY-MM-DD")).diff(moment(today), "days");
      totalDays = 280 - daysToEdd;
    }
    if (totalDays === null || isNaN(totalDays) || totalDays < 0) return null;
    return Math.floor(totalDays / 7) + "w " + (totalDays % 7) + "d";
  };

  // Build the per-fetus array: use saved multiple_pregnancies if present, else `count` blanks.
  buildFetuses = (saved, count) => {
    const n = count > 0 ? count : 1;
    if (Array.isArray(saved) && saved.length > 0) {
      const rows = saved.map((mp) => ({
        delivery_bith_date_time: mp.delivery_bith_date_time != null ? moment(mp.delivery_bith_date_time).format("YYYY-MM-DD HH:mm") : null,
        delivery_gender: mp.delivery_gender || null,
        delivery_birth_weight: mp.delivery_birth_weight || null,
        delivery_birth_height: mp.delivery_birth_height || null,
        delivery_birth_head_cc: mp.delivery_birth_head_cc || null,
        delivery_congenital_abnormality: mp.delivery_congenital_abnormality == "true" || mp.delivery_congenital_abnormality === true,
        delivery_congenital_abnormality_remarks: mp.delivery_congenital_abnormality_remarks || null,
      }));
      // Pad/truncate to the current fetus count.
      while (rows.length < n) rows.push(this.blankFetus());
      return rows.slice(0, n);
    }
    return Array.from({ length: n }, () => this.blankFetus());
  };

  // Refreshes ONLY the snapshot fields that are persisted onto the closed ANC card, without
  // touching the delivery / per-fetus fields the user is currently editing in this modal.
  // Called after the parent ANC card save (onBeforeSave) so the close payload carries the
  // freshest obs/gynae, allergies, conditions, vaccine and investigation data.
  refreshSnapshots = async () => {
    // obs/gynae snapshot (also recomputes GA + no_of_fetus, which are derived/display-only).
    await this.loadObsGyaneContext();
    // Pull the latest saved snapshot fields from the ANC card (allergies/conditions/vaccine/
    // investigation remarks) WITHOUT overwriting the in-progress delivery/fetus inputs.
    try {
      const res = await Axios.post(Get_Anc_Details, this.buildWhereCondition());
      if (res?.data?.status === true && res.data?.data) {
        const d = res.data.data;
        await this.setState({
          current_allergies_snapshot: d?.current_allergies || this.state.current_allergies_snapshot || null,
          current_conditions_snapshot: d?.current_conditions || this.state.current_conditions_snapshot || null,
          vaccine_data_snapshot: d?.vaccine_data || null,
          investigation_remarks_snapshot: d?.investigation_remarks || null,
        });
      }
    } catch (e) {
      // Non-blocking: fall back to whatever snapshots were captured on mount.
    }
    // allergies/conditions from the shared source (OBJECTIVE_GETALL) take precedence when present.
    await this.loadAllergiesAndConditions();
  };

  getPatientAncDetails = async () => {
    const Params = this.buildWhereCondition();
    await Axios.post(Get_Anc_Details, Params)
      .then(async (res) => {
        if (res.data.status === true) {
          const d = res.data?.data;
          if (!d) return;
          await this.setState({
            delivery_mode: d?.delivery_mode || null,
            delivery_type: d?.delivery_type || null,
            delivery_place: d?.delivery_place || null,
            delivery_outcome: d?.delivery_outcome || null,
            delivery_indication_for_c_section: d?.delivery_indication_for_c_section || null,
            caesarean_section_type: d?.caesarean_section_type || null,
            delivery_details: d?.delivery_details || null,
            fetuses: this.buildFetuses(d?.multiple_pregnancies, this.state.no_of_fetus),
            // Preserve the current card's snapshot fields so closing the pregnancy doesn't
            // wipe them (the save below does a create-or-update on the same ANC card row).
            current_allergies_snapshot: d?.current_allergies || null,
            current_conditions_snapshot: d?.current_conditions || null,
            vaccine_data_snapshot: d?.vaccine_data || null,
            investigation_remarks_snapshot: d?.investigation_remarks || null,
          });
          setTimeout(() => {
            this.formRef?.current?.setFieldsValue({
              pregnancy_year: this.state.pregnancy_year,
              delivery_gestational_age: this.state.delivery_gestational_age,
              delivery_mode: this.state.delivery_mode,
              delivery_type: this.state.delivery_type,
              delivery_place: this.state.delivery_place,
              delivery_outcome: this.state.delivery_outcome,
              delivery_indication_for_c_section: this.state.delivery_indication_for_c_section,
              caesarean_section_type: this.state.caesarean_section_type,
              delivery_details: this.state.delivery_details,
            });
          }, 0);
        } else {
          // No saved anc details: still size the fetus blocks to the fetus count.
          await this.setState({ fetuses: this.buildFetuses(null, this.state.no_of_fetus) });
        }
      })
      .catch(() => {
        notification.error({ message: "Something went wrong! Unable to fetch the data", placement: "topRight" });
      });
  };

  disabledPastDate(current) {
    return current && current > moment();
  }

  validateNumber = (rule, value, callback) => {
    const isValidInput = /^(\d{1,2}(\.\d{0,2})?)?$/.test(value) && parseFloat(value) <= 100;
    if (isValidInput || value === "") {
      callback();
    } else {
      callback("Only allowed (maximum 100 and upto two decimals).");
    }
  };

  normalizeValue = (value) => (value === "" || value === undefined ? "" : +value);

  autoHeight = (e) => {
    if (e) e.target.style.height = e.target.scrollHeight + "px";
  };

  onSelectChangeList = async (value, props, field) => {
    await this.setState({ [field]: value });
    if (field === "delivery_type" && value !== "2") {
      await this.setState({ caesarean_section_type: null, delivery_indication_for_c_section: null });
      this.formRef?.current?.setFieldsValue({ caesarean_section_type: null, delivery_indication_for_c_section: null });
    }
  };

  onCaesareanTypeChange = async (e) => {
    const value = e?.target?.value;
    await this.setState({ caesarean_section_type: value });
    this.formRef?.current?.setFieldsValue({ caesarean_section_type: value });
  };

  handleOnTextChange = async (event) => {
    const { name, value } = event.currentTarget;
    await this.setState({ [name]: value });
    this.formRef?.current?.setFieldsValue({ [name]: value });
  };

  // Per-fetus field update.
  onFetusField = async (index, field, value) => {
    await this.setState((prevState) => {
      const fetuses = prevState.fetuses.map((f, i) => (i === index ? { ...f, [field]: value } : f));
      return { fetuses };
    });
  };

  onFetusCongenitalChange = async (index, checked) => {
    await this.setState((prevState) => {
      const fetuses = prevState.fetuses.map((f, i) => (
        i === index
          ? { ...f, delivery_congenital_abnormality: checked, delivery_congenital_abnormality_remarks: checked ? f.delivery_congenital_abnormality_remarks : null }
          : f
      ));
      return { fetuses };
    });
  };

  initSpeech = (info) => {
    const initialText = this.state[info.identifier] != undefined ? this.state[info.identifier] : "";
    if (info.identifier === this.state.speech.identifier) {
      this.setState({ speech: { identifier: info.identifier, state: !this.state.speech.state, initialText } }, () => {
        speechService.init(this.state.speech);
      });
    } else {
      this.setState({ speech: { identifier: this.state.speech.identifier, state: false, initialText: "" } }, () => {
        speechService.init(this.state.speech);
        setTimeout(() => {
          this.setState({ speech: { identifier: info.identifier, state: true, initialText } }, () => {
            speechService.init(this.state.speech);
          });
        }, 1000);
      });
    }
  };

  onFinish = async () => {
    await this.setState({ spin_loading: true });

    // Save the ANC card data FIRST so closing the pregnancy always persists the latest ANC
    // edits (allergies, conditions, vaccines, obs/gynae snapshot, investigations, risk
    // factors, etc.) before the close-save below. The parent (ANC card page) exposes its own
    // save via onBeforeSave; it returns false when its save was aborted (e.g. vaccine
    // validation failed), in which case we must NOT proceed to close the pregnancy.
    if (typeof this.props.onBeforeSave === "function") {
      const ancSaved = await this.props.onBeforeSave();
      if (ancSaved === false) {
        await this.setState({ spin_loading: false });
        return;
      }
      // Re-fetch ONLY the snapshot fields that are carried onto the closed card (obs/gynae,
      // allergies, conditions, vaccines, investigation remarks) so the close payload below
      // reflects the just-saved ANC data. We intentionally do NOT re-run getPatientAncDetails
      // here because it would overwrite the delivery/per-fetus fields the user just entered in
      // this modal with the previously-saved (open-card) values.
      await this.refreshSnapshots();
    }

    // Per-fetus rows go to multiple_pregnancies (one per fetus), each carrying the common
    // delivery mode/place/outcome so the row is self-describing when fetched.
    // Drop fully-blank fetus rows (no birth details captured) so blank padding rows aren't
    // persisted — those caused an extra empty row + wrong fetus numbering in the ANC view.
    const hasFetusData = (f) => f && (
      (f.delivery_gender != null && f.delivery_gender !== "") ||
      (f.delivery_bith_date_time != null && f.delivery_bith_date_time !== "") ||
      (f.delivery_birth_weight != null && f.delivery_birth_weight !== "") ||
      (f.delivery_birth_height != null && f.delivery_birth_height !== "") ||
      (f.delivery_birth_head_cc != null && f.delivery_birth_head_cc !== "") ||
      f.delivery_congenital_abnormality === true ||
      (f.delivery_congenital_abnormality_remarks != null && f.delivery_congenital_abnormality_remarks !== "")
    );
    const multiple_pregnancies = (this.state.fetuses || [])
      .filter(hasFetusData)
      .map((f) => ({
        delivery_type: this.state.delivery_type || null,
        delivery_place: this.state.delivery_place || null,
        delivery_outcome: this.state.delivery_outcome || null,
        delivery_gender: f.delivery_gender || null,
        delivery_bith_date_time: f.delivery_bith_date_time || null,
        delivery_birth_weight: f.delivery_birth_weight || null,
        delivery_birth_height: f.delivery_birth_height || null,
        delivery_birth_head_cc: f.delivery_birth_head_cc || null,
        delivery_congenital_abnormality: f.delivery_congenital_abnormality ? "true" : "false",
        delivery_congenital_abnormality_remarks: f.delivery_congenital_abnormality_remarks || null,
      }));

    // Pregnancy type follows the fetus count: 1 fetus => Single ('1'), else Multiple ('2').
    const pregnancy_type = (this.state.no_of_fetus > 1) ? "2" : "1";

    // Snapshot fields carried over so closing the pregnancy keeps the full card data.
    // current_allergies / current_conditions / vaccine_data come back from Get_Anc_Details as
    // JSON strings (stored in text columns), so pass them through as-is. obs_gyane_data comes
    // from Get_Obs_Gyane_Data as an object, so stringify it (same as the ANC card save flow).
    const current_allergies_snapshot = this.state.current_allergies_snapshot || null;
    const current_conditions_snapshot = this.state.current_conditions_snapshot || null;
    const vaccine_data_snapshot = this.state.vaccine_data_snapshot || null;
    const obs_gyane_data_snapshot = this.state.obs_gyane_data_snapshot
      ? JSON.stringify(this.state.obs_gyane_data_snapshot)
      : null;

    const anc_details = {
      ...this.buildWhereCondition(),
      // Preserve the existing snapshot data on the closed ANC card.
      current_allergies: current_allergies_snapshot,
      current_conditions: current_conditions_snapshot,
      vaccine_data: vaccine_data_snapshot,
      obs_gyane_data: obs_gyane_data_snapshot,
      investigation_remarks: this.state.investigation_remarks_snapshot || null,
      pregnancy_year: this.state.pregnancy_year || null,
      pregnancy_type: pregnancy_type,
      delivery_gestational_age: this.state.delivery_gestational_age || null,
      delivery_mode: this.state.delivery_mode || null,
      delivery_type: this.state.delivery_type || null,
      delivery_place: this.state.delivery_place || null,
      delivery_outcome: this.state.delivery_outcome || null,
      caesarean_section_type: this.state.delivery_type == "2" ? (this.state.caesarean_section_type || null) : null,
      delivery_indication_for_c_section: this.state.delivery_type == "2" ? (this.state.delivery_indication_for_c_section || null) : null,
      delivery_details: this.state.delivery_details || null,
      // Always send per-fetus rows for the multiple_pregnancies table.
      multiple_pregnancies,
      // Who added/recorded this delivery (id + full name with title), mirrored into the
      // Previous Pregnancy Details list so ANC records also show an "Added By".
      added_by_id: LS_SERVICE.get("staff_id"),
      added_by_name: LS_SERVICE.get("profile")?.data?.full_name || ("Dr. " + LS_SERVICE.get("staff_name")),
      created_by: LS_SERVICE.get("staff_id"),
      updated_by: LS_SERVICE.get("staff_id"),
      is_closed : '1'
    };
    const Params = { where_condition: this.buildWhereCondition(), anc_details };
    await Axios.post(Save_Anc_Details, Params)
      .then(async () => {
        notification.success({ message: "Delivery details saved successfully", placement: "topRight" });
        await this.setState({ spin_loading: false });
        // Saving delivery details closes the pregnancy: the backend sets Patient.is_pregnant=0.
        // Reflect it live so the left panel flag and "Pregnancy Active" clear without a reload.
        LS_SERVICE.set("PATIENT_PREGNANT_STATUS", "0");
        pregnantService.init({ is_pregnant: "0" });
        if (this.props.onSaved) this.props.onSaved();
        if (this.props.onClose) this.props.onClose();
      })
      .catch(async () => {
        notification.error({ message: "Something went wrong! Unable to save the data", placement: "topRight" });
        await this.setState({ spin_loading: false });
      });
  };

  render() {
    const { visible, onClose } = this.props;
    return (
      <Modal
        title="Delivery Details"
        visible={visible}
        onCancel={onClose}
        width="1100px"
        className="emrfrm"
        footer={false}
        destroyOnClose={false}
      >
        <Form layout="vertical" className="emrfrm" ref={this.formRef} onFinish={this.onFinish}>
          <DeliveryDetails
            allowEdit={this.state.allowEdit}
            values={{
              pregnancy_year: this.state.pregnancy_year,
              delivery_gestational_age: this.state.delivery_gestational_age,
              delivery_mode: this.state.delivery_mode,
              delivery_type: this.state.delivery_type,
              delivery_place: this.state.delivery_place,
              delivery_outcome: this.state.delivery_outcome,
              delivery_indication_for_c_section: this.state.delivery_indication_for_c_section,
              caesarean_section_type: this.state.caesarean_section_type,
              delivery_details: this.state.delivery_details,
            }}
            fetuses={this.state.fetuses}
            deliveryOptions={this.state.delivery_options}
            outcomeOptions={this.state.outcome_options}
            placeOptions={this.state.place_options}
            genderOptions={this.state.gender_options}
            speech={this.state.speech}
            onSelectChangeList={this.onSelectChangeList}
            onTextChange={this.handleOnTextChange}
            onCaesareanTypeChange={this.onCaesareanTypeChange}
            onFetusField={this.onFetusField}
            onFetusCongenitalChange={this.onFetusCongenitalChange}
            initSpeech={this.initSpeech}
            autoHeight={this.autoHeight}
            validateNumber={this.validateNumber}
            normalizeValue={this.normalizeValue}
            disabledPastDate={this.disabledPastDate}
          />
          <div className="row mt-4 mb-2">
            <div className="col-12 text-center">
              <Form.Item>
                <button htmlType="submit" className="btn btn-primary px-5" disabled={this.state.spin_loading || !this.state.allowEdit}>
                  SAVE {this.state.spin_loading ? LOADER : null}
                </button>
              </Form.Item>
            </div>
          </div>
        </Form>
      </Modal>
    );
  }
}

export default DeliveryDetailsModal;
